import { Helmet } from 'react-helmet-async';
import { AgGridReact } from 'ag-grid-react';
import { useState, useEffect, useRef, useMemo } from 'react';
import { enqueueSnackbar } from 'notistack';
import { Box, Button, CircularProgress, Stack, Typography } from '@mui/material';
import axiosInstance from '../../utils/axios';
import EditIconButton from '../../reuseable-components/DefaultButtons/EditIconButton';
import DeleteIconButton from '../../reuseable-components/DefaultButtons/DeleteIconButton';
import PurchasingGroupForm from './PurchasingGroupForm';
import Iconify from '../../reuseable-components/iconify/Iconify';
import AgGridDefaults from '../../reuseable-components/AGGrid/AGGridDefaults';
import AGGridQuickFilterWrapper from '../../reuseable-components/AGGrid/AGGridQuickFilterWrapper';

export default function PurchasingGroupList()
{
  const gridRef = useRef(null);

  const [purchasingGroups, setPurchasingGroups] = useState([]);
  const [netTerms, setNetTerms] = useState([]);
  const [integrationTypes, setIntegrationTypes] = useState([]);
  const [editPurchasingGroup, setEditPurchasingGroup] = useState(null);
  const [openForm, setOpenForm] = useState(false);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    setLoading(true);
    axiosInstance.get("/purchasingGroup/purchasingGroups")
      .then(res => {
        setPurchasingGroups(res.data.purchasingGroups);
        setNetTerms(res.data.netTerms);
        setIntegrationTypes(res.data.integrationTypes);
      })
      .catch(() => enqueueSnackbar('Purchasing group list failed', { variant: 'error' }))
      .finally(() => setLoading(false));
  }, []);

  const deletePurchasingGroup = (id) => {
    axiosInstance.post(`/purchasingGroup/deletePurchasingGroup?id=${id}`)
      .then(() => {
        setPurchasingGroups(purchasingGroups => [...purchasingGroups.filter(purchasingGroup => purchasingGroup.id !== id)]);
        enqueueSnackbar('Purchasing group deleted', { variant: 'success' })
      })
      .catch(() => enqueueSnackbar('Purchasing group delete failed', { variant: 'error' }))
  };

  const columns = useMemo(() => [
    {
      field: 'name',
      headerName: 'Name',
      minWidth: 150,
      flex: 1
    },
    {
      field: 'streetAddress',
      headerName: 'Address',
      minWidth: 100,
      flex: 1
    },
    {
      field: 'state',
      minWidth: 100,
    },
    {
      field: 'city',
      minWidth: 150,
    },
    {
      field: 'zip',
      headerName: 'Zip',
      minWidth: 100,
    },
    {
      field: 'actions',
      headerName: 'Actions',
      width: 100,
      cellRenderer: (params) => <Stack direction="row" spacing={1}>
        <EditIconButton onClick={() => { setEditPurchasingGroup(params.data); setOpenForm(true); }} />
        <DeleteIconButton content={`Are you sure you want to delete ${params.data.name}?`} action={() => deletePurchasingGroup(params.data.id)} />
      </Stack>
    }
  ], []);

  return (<>
    <Helmet>
      <title>Purchasing Group List | NHIDirect</title>
    </Helmet>

    <Box sx={{ height: "calc(100% - 55px)", mt: 2 }}>
      <AGGridQuickFilterWrapper gridRef={gridRef}>
        <Button variant="contained" startIcon={<Iconify icon="eva:plus-fill" />} onClick={() => { setEditPurchasingGroup(null); setOpenForm(true); }} color="primary" size="small">
          <Typography>Add Purchasing Group</Typography>
        </Button>
      </AGGridQuickFilterWrapper>   
      {!loading ? <Box sx={{ height: "max(500px, 100%)" }}>
        <AgGridReact ref={gridRef} {...AgGridDefaults} columnDefs={columns} rowData={purchasingGroups} loading={loading} />
      </Box> : <Box sx={{ display: "flex", justifyContent: "center", m: 3 }}><CircularProgress /></Box>}
      <PurchasingGroupForm netTerms={netTerms} editPurchasingGroup={editPurchasingGroup} open={openForm} integrationTypes={integrationTypes}
        close={() => { setEditPurchasingGroup(null); setOpenForm(false); }} setPurchasingGroups={setPurchasingGroups} />
    </Box>
  </>);
}
